import { supabase } from './supabase';

const ASSET_PHOTOS_BUCKET = 'asset-photos';

/**
 * Upload an asset photo to Supabase Storage
 */
export async function uploadAssetPhoto(
  organizationId: string,
  assetId: string,
  file: File
): Promise<{ url: string; path: string } | null> {
  try {
    // Create a unique file path: org-id/asset-id/timestamp-filename
    const timestamp = Date.now();
    const fileExt = file.name.split('.').pop();
    const fileName = `${timestamp}.${fileExt}`;
    const filePath = `${organizationId}/${assetId}/${fileName}`;

    // Upload file
    const { data, error } = await supabase.storage
      .from(ASSET_PHOTOS_BUCKET)
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false,
      });

    if (error) {
      console.error('Error uploading photo:', error);
      return null;
    }

    // Get public URL
    const {
      data: { publicUrl },
    } = supabase.storage.from(ASSET_PHOTOS_BUCKET).getPublicUrl(data.path);

    return {
      url: publicUrl,
      path: data.path,
    };
  } catch (error) {
    console.error('Error in uploadAssetPhoto:', error);
    return null;
  }
}

/**
 * Delete an asset photo from Supabase Storage
 */
export async function deleteAssetPhoto(photoPath: string): Promise<boolean> {
  try {
    const { error } = await supabase.storage.from(ASSET_PHOTOS_BUCKET).remove([photoPath]);

    if (error) {
      console.error('Error deleting photo:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error in deleteAssetPhoto:', error);
    return false;
  }
}

/**
 * Get the storage path from a public URL
 */
export function getPhotoPathFromUrl(url: string): string | null {
  try {
    // Extract path from URL: https://.../storage/v1/object/public/asset-photos/path
    const match = url.match(/asset-photos\/(.+)$/);
    return match ? match[1] : null;
  } catch {
    return null;
  }
}

/**
 * Validate image file
 */
export function validateImageFile(file: File): {
  valid: boolean;
  error?: string;
} {
  const maxSize = 5 * 1024 * 1024; // 5MB
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];

  if (!allowedTypes.includes(file.type)) {
    return {
      valid: false,
      error: 'Invalid file type. Only JPEG, PNG, GIF, and WebP images are allowed.',
    };
  }

  if (file.size > maxSize) {
    return {
      valid: false,
      error: 'File size exceeds 5MB limit.',
    };
  }

  return { valid: true };
}
